import { createBirthYearSelectValues } from 'common/birthYearCreator';
import { ReactError } from 'consts/ReactError';

export class Validator<T> {
	value: T;
	error: string;
	constructor(value: T) {
		this.value = value;
		this.error = '';
	}

	/**
	 * 必須チェック
	 * @param errorMessage カスタムエラーメッセージ
	 * @returns this
	 */
	require(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (this.value == null) {
			this.error = errorMessage ?? ReactError.E0001;
			return this;
		}

		if (typeof this.value === 'string' && this.value === '') {
			this.error = errorMessage ?? ReactError.E0001;
			return this;
		}

		this.error = '';
		return this;
	}

	/**
	 * ※string限定
	 * 全角文字列チェック
	 * @param errorMessage カスタムエラーメッセージ
	 * @returns this
	 */
	charTypeFullWidth(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (typeof this.value !== 'string') {
			return this;
		}

		// 空文字は許容
		if (this.value === '') {
			return this;
		}

		if (!this.value.match(/^([^\x01-\x7E\uFF61-\uFF9F]{1,100})$/)) {
			this.error = errorMessage ?? `100文字以内で全角で入力してください。`;
			return this;
		}
		this.error = '';
		return this;
	}

	/**
	 * ※string限定
	 * 全角カナ文字列チェック
	 * @param errorMessage カスタムエラーメッセージ
	 * @returns this
	 */
	charTypeFullWidthKana(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (typeof this.value !== 'string') {
			return this;
		}

		// 空文字は許容
		if (this.value === '') {
			return this;
		}

		if (!this.value.match(/^([ァ-ヶー　]{1,100})$/)) {
			this.error = errorMessage ?? `100文字以内で全角カナで入力してください。`;
			return this;
		}
		this.error = '';
		return this;
	}

	charTypeNickName(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (typeof this.value !== 'string') {
			return this;
		}

		// 空文字は許容
		if (this.value === '') {
			return this;
		}

		if (!this.value.match(/^([^\x01-\x7E\uFF61-\uFF9F]{1,10})$/)) {
			this.error = errorMessage ?? `10文字以内で全角で入力してください。`;
			return this;
		}
		this.error = '';
		return this;
	}

	/**
	 * ※string限定
	 * 数値文字列チェック
	 * @param errorMessage カスタムエラーメッセージ
	 * @returns this
	 */
	charTypeNumber(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (typeof this.value !== 'string') {
			return this;
		}

		// 空文字は許容
		if (this.value === '') {
			return this;
		}

		if (!this.value.match(/^([1-9]\d*|0)$/)) {
			this.error = errorMessage ?? `半角数字で入力してください。`;
			return this;
		}
		this.error = '';
		return this;
	}

	charTypeZip(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (typeof this.value !== 'string') {
			return this;
		}

		// 空文字は許容
		if (this.value === '') {
			return this;
		}

		if (!this.value.match(/^[0-9]{7}$/)) {
			this.error = errorMessage ?? `ハイフンなしの半角7文字で入力してください。`;
			return this;
		}
		this.error = '';
		return this;
	}

	charTypeTel(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (typeof this.value !== 'string') {
			return this;
		}

		// 空文字は許容
		if (this.value === '') {
			return this;
		}

		if (!this.value.match(/^[0-9-]{1,13}$/)) {
			this.error = errorMessage ?? `半角13文字以内で入力してください。`;
			return this;
		}
		this.error = '';
		return this;
	}

	/**
	 * ※string限定
	 * 半角英大文字/小文字/数字/記号のいずれかを使った合計8文字以上60文字以下のチェック
	 * @param errorMessage カスタムエラーメッセージ
	 * @returns this
	 */
	charTypeGakkenId(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (typeof this.value !== 'string') {
			return this;
		}

		// 空文字は許容
		if (this.value === '') {
			return this;
		}

		if (!this.value.match(/^([a-zA-Z0-9@\._\-!#\$%&'\*\+\/=\?\^`\{\|\}~]{8,60})$/)) {
			this.error =
				errorMessage ?? `8文字以上60文字以下の半角英数字や記号（-_.@!#$%&'*+/=?^\`{|}~）で入力してください。`;
			return this;
		}
		this.error = '';
		return this;
	}

	/**
	 * ※string限定
	 * 半角英大文字/小文字/数字/記号それぞれ最低1文字以上、合計8文字以上32文字以下のチェック
	 * @param errorMessage カスタムエラーメッセージ
	 * @returns this
	 */
	charTypePassword(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (typeof this.value !== 'string') {
			return this;
		}

		// 空文字は許容
		if (this.value === '') {
			return this;
		}

		if (!this.value.match(/^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?\d)[a-zA-Z\d@._-]{8,32}$/)) {
			this.error =
				errorMessage ??
				`「大文字」「小文字」「数字」を1文字以上使用し半角英数字8文字以上32文字以下で入力してください。`;
			return this;
		}
		this.error = '';
		return this;
	}

	/**
	 * ※string限定
	 * メールアドレスに＠が含まれているかのチェック
	 * @param errorMessage カスタムエラーメッセージ
	 * @returns this
	 */
	charTypeEmail(errorMessage?: string): this {
		if (this.error !== '') {
			return this;
		}

		if (typeof this.value !== 'string') {
			return this;
		}

		// 空文字は許容
		if (this.value === '') {
			return this;
		}

		// [英数字+記号]@[ドメイン] の形式かつ254文字以下でない場合エラー
		if (
			!this.value.match(
				/^(?!(?:(?:\x22?\x5C[\x00-\x7E]\x22?)|(?:\x22?[^\x5C\x22]\x22?)){255,})(?!(?:(?:\x22?\x5C[\x00-\x7E]\x22?)|(?:\x22?[^\x5C\x22]\x22?)){65,}@)(?:(?:[\x21\x23-\x27\x2A\x2B\x2D\x2F-\x39\x3D\x3F\x5E-\x7E]+)|(?:\x22(?:[\x01-\x08\x0B\x0C\x0E-\x1F\x21\x23-\x5B\x5D-\x7F]|(?:\x5C[\x00-\x7F]))*\x22))(?:\.(?:(?:[\x21\x23-\x27\x2A\x2B\x2D\x2F-\x39\x3D\x3F\x5E-\x7E]+)|(?:\x22(?:[\x01-\x08\x0B\x0C\x0E-\x1F\x21\x23-\x5B\x5D-\x7F]|(?:\x5C[\x00-\x7F]))*\x22)))*@(?:(?:(?!.*[^.]{64,})(?:(?:(?:xn--)?[a-z0-9]+(?:-+[a-z0-9]+)*\.){1,126}){1,}(?:(?:[a-z][a-z0-9]*)|(?:(?:xn--)[a-z0-9]+))(?:-+[a-z0-9]+)*)|(?:\[(?:(?:IPv6:(?:(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){7})|(?:(?!(?:.*[a-f0-9][:\]]){7,})(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,5})?::(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,5})?)))|(?:(?:IPv6:(?:(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){5}:)|(?:(?!(?:.*[a-f0-9]:){5,})(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,3})?::(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,3}:)?)))?(?:(?:25[0-5])|(?:2[0-4][0-9])|(?:1[0-9]{2})|(?:[1-9]?[0-9]))(?:\.(?:(?:25[0-5])|(?:2[0-4][0-9])|(?:1[0-9]{2})|(?:[1-9]?[0-9]))){3}))\]))$/i,
			) ||
			this.value.indexOf('&') !== -1 ||
			this.value.length > 254
		) {
			this.error = errorMessage ?? ReactError.E0004;
			return this;
		}
		this.error = '';
		return this;
	}

	end(): string {
		return this.error;
	}
}

// 生年月日の妥当性チェック
export const isBirthCheckOk = (y: string, m: string, d: string) => {
	// 年月日のどれかが入力されていない場合はエラー
	if (!y || !m || !d) {
		return false;
	}

	// 年が1901-現在年以外ならエラー
	const yearSelectValues = createBirthYearSelectValues();
	if (!yearSelectValues.includes(Number(y))) {
		return false;
	}

	const birth = new Date(Number(y), Number(m) - 1, Number(d));
	const year = birth.getFullYear();
	const month = birth.getMonth() + 1;
	const day = birth.getDate();
	// 日付変換前の値と違ったらエラー
	if (year !== Number(y) || month !== Number(m) || day !== Number(d)) {
		return false;
	}

	// 現在日よりも生年月日が新しい（後の）場合エラー
	const now = new Date();
	if (now < birth) {
		return false;
	}

	return true;
};
